import { Observable ,  Subject } from 'rxjs';

export interface CacheService {
    has(key: string): boolean;
    set(key: string, value: any, maxAge?: number): void;
    get<T>(key: string): T;
    remove(key:string):void;
}

export interface MemoryCacheItem {
    expirationLength: number;
    dateCreated: number;
    value: any;
}

export class MemoryCache implements CacheService {

    public _storage: Map<string, MemoryCacheItem> = new Map<string, MemoryCacheItem>();
    public readonly DEFAULT_MAX_AGE: number = 300000;

    constructor(public _keyPrefix: string = "") { }

    /**
     * Returns whether or not the storage has this key
     * @param key
     */
    public has(key: string): boolean {
        let internalKey = this.getKey(key);

        if (!this._storage.has(internalKey))
            return false;

        let memoryCachedItem = this._storage.get(internalKey);

        if (this.isExpired(memoryCachedItem))
            return false;

        return true;
    }

    /**
     * Set the value with the provided key
     * @param key Key
     * @param value Object to store
     * @param maxAge Length to store
     */
    public set(key: string, value: any, maxAge: number = this.DEFAULT_MAX_AGE): void {
        this._storage.set(this.getKey(key), { expirationLength: maxAge, dateCreated: new Date().getTime(), value: value });
    }

    /**
     * Gets the value from the internal storage by the provided key.
     * @param key Key
     */
    public get<T>(key: string): T {
        let memoryCacheItem: MemoryCacheItem;
        let internalKey = this.getKey(key);

        if (!this._storage.has(internalKey)) {
            console.warn(`Cache ${internalKey} does not exist.`)
            return;
        }

        memoryCacheItem = this._storage.get(internalKey);

        if (this.isExpired(memoryCacheItem)) {
            console.warn(`Cache ${internalKey} has expired.`);
            return;
        }

        return memoryCacheItem.value as T;
    }

    /**
     * Remove the item from the internal storage 
     * @param key
     */
    public remove(key: string): void {
        let internalKey = this.getKey(key);

        if (!this.has(internalKey))
            return;

        this._storage.delete(internalKey);
    }

    /**
     * Returns 'internal' key
     * @param key
     */
    public getKey(key: string): string {
        return `${this._keyPrefix}${key}`;
    }

    public isExpired(memoryCacheItem: MemoryCacheItem): boolean {
        return memoryCacheItem.dateCreated + memoryCacheItem.expirationLength < new Date().getTime();
    }
}